import React from "react";
import { useParams, Navigate } from "react-router-dom";
import {
  Terminal,
  Cpu,
  Zap,
  Shield,
  Users,
  GitBranch,
  Share2,
  Activity,
  Eye,
  MessageSquare,
  Search,
  AlertCircle,
  Layers,
  Box,
  Workflow,
} from "lucide-react";
import { Section, Button, Card, Badge } from "@vild/site-ui";

// --- RUNTIME PAGE ---
const RuntimePage = () => (
  <>
    <div className="relative pt-32 pb-20 overflow-hidden">
      <div className="absolute inset-0 bg-zinc-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(34,197,94,0.05)_0%,transparent_100%)]"></div>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        ></div>
      </div>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="flex flex-col md:flex-row gap-12 items-center">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-xs font-mono text-green-400 mb-6">
              <Terminal className="w-3 h-3" /> Serverless Runtime
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white mb-6">
              Zero infra.
              <br />
              Infinite scale.
            </h1>
            <p className="text-xl text-zinc-400 mb-8 max-w-lg">
              Spawns workers on demand. Pay only for what you use. No idle servers, no capacity planning.
            </p>
            <div className="flex gap-4">
              <Button size="lg" asLink="/signup">
                Deploy a Pipeline
              </Button>
              <Button size="lg" variant="outline" asLink="/docs/runtime">
                View Specs
              </Button>
            </div>
          </div>
          <div className="flex-1 w-full">
            <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-4 font-mono text-sm shadow-2xl">
              <div className="flex items-center gap-2 mb-4 border-b border-zinc-800 pb-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/20"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500/20"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500/20"></div>
                </div>
                <span className="text-zinc-500 text-xs ml-2">runner-us-east-1</span>
              </div>
              <div className="space-y-2 text-zinc-400">
                <div>
                  <span className="text-green-500">➜</span>{" "}
                  <span className="text-zinc-200">vild run pipeline:render-4k</span>
                </div>
                <div>[info] Spawning worker instance (t4g.xlarge)...</div>
                <div>[info] Pulling image vild/processor:latest...</div>
                <div>
                  [info] Input: <span className="text-blue-400">s3://raw/footage_001.mov</span> (4.2GB)
                </div>
                <div>
                  [proc] Transcoding... <span className="text-green-400">================&gt; 100%</span>
                </div>
                <div>
                  [info] Uploading to <span className="text-blue-400">s3://final/footage_001.mp4</span>
                </div>
                <div className="text-zinc-500">Done in 14.2s. Cost: $0.042</div>
                <div className="animate-pulse">_</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Section className="border-t border-zinc-900 bg-zinc-900/20">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="p-6 rounded-2xl bg-zinc-950 border border-zinc-800">
          <Cpu className="w-8 h-8 text-green-500 mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Auto-scaling</h3>
          <p className="text-zinc-400">
            We analyze your pipeline&apos;s complexity and automatically provision the right compute resources, from CPU instances to GPU clusters.
          </p>
        </div>
        <div className="p-6 rounded-2xl bg-zinc-950 border border-zinc-800">
          <Zap className="w-8 h-8 text-green-500 mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Watch Folders</h3>
          <p className="text-zinc-400">
            Connect an S3 bucket or Google Drive folder. The moment a file lands, your pipeline triggers automatically.
          </p>
        </div>
        <div className="p-6 rounded-2xl bg-zinc-950 border border-zinc-800">
          <Activity className="w-8 h-8 text-green-500 mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Cost Per Second</h3>
          <p className="text-zinc-400">
            Stop paying for idle servers. Our runtime bills by the millisecond of execution time, down to the penny.
          </p>
        </div>
      </div>
    </Section>
  </>
);

// --- GATES PAGE ---
const GatesPage = () => (
  <>
    <Section className="py-24 text-center">
      <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 mb-6 px-3 py-1 text-sm">Human-in-the-Loop</Badge>
      <h1 className="text-5xl md:text-6xl font-bold mb-6">Trust, but verify.</h1>
      <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-12">
        Insert approval gates anywhere in your pipeline. Let AI do the heavy lifting, but keep humans in control of the final output.
      </p>

      <div className="max-w-4xl mx-auto bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden shadow-2xl text-left">
        <div className="bg-zinc-800/50 p-4 border-b border-zinc-800 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center font-bold text-white">V</div>
            <div>
              <div className="text-sm font-medium text-white">Vild Bot</div>
              <div className="text-xs text-zinc-500">Pipeline requires approval</div>
            </div>
          </div>
          <div className="text-xs font-mono text-zinc-500">10:42 AM</div>
        </div>
        <div className="p-6 md:p-8 flex flex-col md:flex-row gap-8">
          <div className="flex-1">
            <div className="aspect-video bg-zinc-950 rounded border border-zinc-800 flex items-center justify-center mb-4">
              <span className="text-zinc-600 font-mono">Video Preview Player</span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-zinc-500 text-xs uppercase">Confidence</div>
                <div className="text-green-400 font-mono">98.2%</div>
              </div>
              <div>
                <div className="text-zinc-500 text-xs uppercase">Generated Tags</div>
                <div className="text-zinc-300">#viral #action #4k</div>
              </div>
            </div>
          </div>
          <div className="md:w-64 flex flex-col justify-center gap-3">
            <h3 className="font-medium text-white mb-2">Action Required</h3>
            <p className="text-sm text-zinc-400 mb-4">Review the generated captions before burning them into the final render.</p>
            <Button variant="success" className="w-full">
              Approve & Continue
            </Button>
            <Button variant="destructive" className="w-full">
              Reject & Stop
            </Button>
          </div>
        </div>
      </div>
    </Section>

    <Section className="grid grid-cols-1 md:grid-cols-2 gap-12">
      <div className="space-y-8">
        <div>
          <h3 className="text-2xl font-bold text-white mb-3 flex items-center gap-3">
            <Shield className="text-blue-500" /> Remote Gates
          </h3>
          <p className="text-zinc-400">
            Reviewers don&apos;t need a Vild account. Send secure, magic links via Email, Slack, or Microsoft Teams. They click a button, and the pipeline resumes.
          </p>
        </div>
        <div>
          <h3 className="text-2xl font-bold text-white mb-3 flex items-center gap-3">
            <GitBranch className="text-blue-500" /> Conditional Logic
          </h3>
          <p className="text-zinc-400">
            Set thresholds. If AI confidence is &gt;95%, skip the gate. If it&apos;s lower, route to a human for manual review.
          </p>
        </div>
        <div>
          <h3 className="text-2xl font-bold text-white mb-3 flex items-center gap-3">
            <MessageSquare className="text-blue-500" /> Gate Notes
          </h3>
          <p className="text-zinc-400">
            Discuss results with your team, @mention teammates, and attach lightweight annotations before resuming the pipeline.
          </p>
        </div>
      </div>
      <Card className="bg-zinc-900/30 border-dashed border-zinc-700 flex items-center justify-center p-12">
        <div className="text-center">
          <h4 className="text-lg font-medium text-white mb-2">Ready to try it?</h4>
          <p className="text-zinc-500 text-sm mb-4">Start with the Auto-Caption pipeline</p>
          <Button size="sm" asLink="/signup?starter=auto-caption">
            Use Template
          </Button>
        </div>
      </Card>
    </Section>
  </>
);

// --- COMPOSITION PAGE ---
const CompositionPage = () => (
  <>
    <div className="pt-32 pb-20 px-6">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
        <div className="lg:col-span-2">
          <h1 className="text-5xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
            Modular by design.
          </h1>
          <p className="text-xl text-zinc-400 mb-8">
            Stop writing spaghetti scripts. Compose sophisticated media pipelines using our node-based visual editor or strongly-typed SDK.
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded bg-zinc-800 flex items-center justify-center">
                <Box className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <div className="text-white font-medium">50+ Standard Nodes</div>
                <div className="text-xs text-zinc-500">Video Processing, Image Manipulation, OpenAI, etc.</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded bg-zinc-800 flex items-center justify-center">
                <Layers className="w-4 h-4 text-pink-400" />
              </div>
              <div>
                <div className="text-white font-medium">Reusable Modules</div>
                <div className="text-xs text-zinc-500">Package sub-pipelines as custom nodes.</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 h-[500px] bg-zinc-900 rounded-xl border border-zinc-800 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(#3f3f46_1px,transparent_1px)] [background-size:16px_16px] opacity-20"></div>

          <div className="absolute top-1/2 left-10 -translate-y-1/2 w-40 bg-zinc-800 rounded-lg border border-zinc-700 p-3 shadow-lg z-10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-zinc-300">Trigger</span>
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
            </div>
            <div className="text-xs text-zinc-500">S3 Watch Folder</div>
          </div>

          <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
            <path d="M200 250 C 250 250, 250 150, 320 150" fill="none" stroke="#52525b" strokeWidth="2" />
            <path d="M200 250 C 250 250, 250 350, 320 350" fill="none" stroke="#52525b" strokeWidth="2" />
          </svg>

          <div className="absolute top-[120px] left-[320px] w-40 bg-zinc-800 rounded-lg border border-zinc-700 p-3 shadow-lg z-10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-zinc-300">Transcode</span>
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
            </div>
            <div className="text-xs text-zinc-500">H.264 1080p</div>
          </div>

          <div className="absolute top-[320px] left-[320px] w-40 bg-zinc-800 rounded-lg border border-zinc-700 p-3 shadow-lg z-10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-zinc-300">AI Analyze</span>
              <div className="w-2 h-2 rounded-full bg-purple-500"></div>
            </div>
            <div className="text-xs text-zinc-500">Frame Sampling</div>
          </div>

          <div className="absolute bottom-4 right-4 bg-zinc-950/80 backdrop-blur px-3 py-1.5 rounded text-xs text-zinc-400 border border-zinc-800">
            Drag to connect
          </div>
        </div>
      </div>
    </div>

    <Section className="text-center">
      <h2 className="text-3xl font-bold mb-8">Power user? Bring your own code.</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        <Card className="text-left">
          <div className="font-mono text-xs text-yellow-500 mb-2">PYTHON SDK</div>
          <pre className="text-xs text-zinc-300 overflow-x-auto p-4 bg-zinc-950 rounded border border-zinc-800 mb-4">{`from vild import Pipeline, Node

def build_pipeline():
  p = Pipeline("My Resize Flow")
  src = p.add(Node.S3Source("bucket-in"))
  resize = p.add(Node.Video("resize", width=1920))
  
  src.connect(resize)
  return p`}</pre>
          <p className="text-sm text-zinc-400">Define pipelines as code and deploy via CI/CD.</p>
        </Card>
        <Card className="text-left">
          <div className="font-mono text-xs text-blue-500 mb-2">CUSTOM DOCKER NODES</div>
          <div className="h-32 flex items-center justify-center bg-zinc-950 rounded border border-zinc-800 mb-4 text-zinc-500 text-sm italic">
            Dockerfile visual placeholder
          </div>
          <p className="text-sm text-zinc-400">Package any script or binary as a node. If it runs on Linux, it runs on Vild.</p>
        </Card>
      </div>
    </Section>
  </>
);

// --- OBSERVABILITY PAGE ---
const ObservabilityPage = () => (
  <>
    <Section className="py-24">
      <div className="text-center mb-16">
        <h1 className="text-5xl font-bold mb-6">Total Clarity.</h1>
        <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
          Debug complex media workflows in seconds. Trace every frame, every credit spent, and every error log.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl p-6 h-96 relative overflow-hidden">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold text-white flex items-center gap-2">
              <Activity className="w-4 h-4 text-orange-500" /> Pipeline Activity
            </h3>
            <div className="flex gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500"></div>
              <div className="w-2 h-2 rounded-full bg-zinc-700"></div>
              <div className="w-2 h-2 rounded-full bg-zinc-700"></div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-64 flex items-end justify-between px-6 pb-6 gap-2">
            {[40, 65, 30, 80, 55, 90, 45, 60, 75, 50, 85, 95, 60, 40, 70].map((h, i) => (
              <div key={i} className="w-full bg-zinc-800 hover:bg-orange-500/50 transition-colors rounded-t" style={{ height: `${h}%` }}></div>
            ))}
          </div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-bold text-white mb-6 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" /> Recent Failures
          </h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 rounded bg-zinc-950/50 border border-zinc-800/50">
              <div className="mt-1 w-2 h-2 rounded-full bg-red-500 shrink-0"></div>
              <div>
                <div className="text-sm font-medium text-white">Render Timeout</div>
                <div className="text-xs text-zinc-500">Pipeline: Social Clips • 2m ago</div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded bg-zinc-950/50 border border-zinc-800/50">
              <div className="mt-1 w-2 h-2 rounded-full bg-red-500 shrink-0"></div>
              <div>
                <div className="text-sm font-medium text-white">Missing Asset</div>
                <div className="text-xs text-zinc-500">Pipeline: Intro Gen • 15m ago</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
        <div className="flex gap-4 items-start">
          <div className="p-3 bg-zinc-800 rounded-lg">
            <Eye className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h4 className="text-lg font-bold text-white mb-1">Visual Debugger</h4>
            <p className="text-zinc-400">
              Click any failed run to replay it step-by-step. Inspect intermediate outputs at every node.
            </p>
          </div>
        </div>
        <div className="flex gap-4 items-start">
          <div className="p-3 bg-zinc-800 rounded-lg">
            <Search className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h4 className="text-lg font-bold text-white mb-1">Centralized Logs</h4>
            <p className="text-zinc-400">
              Aggregate stdout/stderr from all nodes into a single searchable stream. Pipe to Datadog or Splunk.
            </p>
          </div>
        </div>
      </div>
    </Section>
  </>
);

// --- COLLABORATION PAGE ---
const CollaborationPage = () => (
  <>
    <Section className="py-24 text-center">
      <h1 className="text-5xl font-bold mb-6">Build together.</h1>
      <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-12">
        Pipelines are team sports. Share logic, review outputs, and manage permissions across your entire organization.
      </p>

      <div className="max-w-3xl mx-auto bg-zinc-900 rounded-xl border border-zinc-800 p-8 shadow-2xl relative">
        <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full border-4 border-zinc-950 bg-blue-500 flex items-center justify-center text-white font-bold z-10">
          JD
        </div>
        <div className="absolute -bottom-4 -right-4 w-12 h-12 rounded-full border-4 border-zinc-950 bg-pink-500 flex items-center justify-center text-white font-bold z-10">
          AL
        </div>

        <div className="space-y-6 text-left">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 rounded-full bg-zinc-700 flex-shrink-0"></div>
            <div className="bg-zinc-800 p-4 rounded-r-lg rounded-bl-lg">
              <p className="text-zinc-300 text-sm">
                I updated the color grading node. Can you check the output on the latest batch?
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4 flex-row-reverse">
            <div className="w-8 h-8 rounded-full bg-zinc-700 flex-shrink-0"></div>
            <div className="bg-blue-900/20 border border-blue-900/50 p-4 rounded-l-lg rounded-br-lg">
              <p className="text-blue-100 text-sm">Looks great! The skin tones are much better. Merging into production.</p>
            </div>
          </div>
        </div>
      </div>
    </Section>

    <Section className="grid grid-cols-1 md:grid-cols-3 gap-8">
      <Card>
        <Users className="w-8 h-8 text-teal-400 mb-4" />
        <h3 className="text-lg font-bold text-white mb-2">Team Permissions</h3>
        <p className="text-sm text-zinc-400">RBAC built-in. Assign Viewer, Editor, or Admin roles per workspace.</p>
      </Card>
      <Card>
        <Share2 className="w-8 h-8 text-teal-400 mb-4" />
        <h3 className="text-lg font-bold text-white mb-2">Shared Library</h3>
        <p className="text-sm text-zinc-400">Publish common nodes and presets for your team to reuse. Don&apos;t reinvent the wheel.</p>
      </Card>
      <Card>
        <Workflow className="w-8 h-8 text-teal-400 mb-4" />
        <h3 className="text-lg font-bold text-white mb-2">Project Spaces</h3>
        <p className="text-sm text-zinc-400">
          Isolate client work. Give each client their own workspace with separate billing and access.
        </p>
      </Card>
    </Section>
  </>
);

export const FeatureTemplate: React.FC = () => {
  const { slug } = useParams();

  switch (slug) {
    case "runtime":
      return <RuntimePage />;
    case "gates":
      return <GatesPage />;
    case "composition":
      return <CompositionPage />;
    case "observability":
      return <ObservabilityPage />;
    case "collaboration":
      return <CollaborationPage />;
    default:
      return <Navigate to="/" replace />;
  }
};

